import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Label,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  ReferenceLine,
  LabelList,
} from "recharts";
import { TrendingUp, Warning, ShowChart } from "@mui/icons-material";

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const changePercent = Number.parseFloat(data.changePercent);

    return (
      <div
        style={{
          backgroundColor: "white",
          padding: "12px",
          border: "1px solid #e0e0e0",
          borderRadius: "4px",
          boxShadow: "0 3px 8px rgba(0,0,0,0.15)",
        }}
      >
        <h5
          style={{
            fontWeight: 600,
            marginTop: 0,
            marginBottom: "8px",
            color: "#333",
            borderBottom: "1px solid #eee",
            paddingBottom: "6px",
          }}
        >
          {data.periodName}
        </h5>
        <div style={{ fontSize: "13px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "6px",
            }}
          >
            <span style={{ color: "#666", marginRight: "24px" }}>
              Total Paid:
            </span>
            <span style={{ fontWeight: 600, color: "hsl(var(--chart-1))" }}>
              RWF {data.formattedTotal}
            </span>
          </div>

          {data.periodId !== data.firstPeriodId && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "6px",
              }}
            >
              <span style={{ color: "#666", marginRight: "24px" }}>
                MoM Change:
              </span>
              <span
                style={{
                  fontWeight: 600,
                  color: changePercent > 0 ? "#2e7d32" : "#d32f2f",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {changePercent > 0 ? (
                  <TrendingUp style={{ fontSize: 14, marginRight: "4px" }} />
                ) : (
                  <ShowChart style={{ fontSize: 14, marginRight: "4px" }} />
                )}
                {changePercent > 0 ? "+" : ""}
                {data.changePercent}%
              </span>
            </div>
          )}

          {data.significance && (
            <div
              style={{
                marginTop: "8px",
                paddingTop: "6px",
                borderTop: "1px solid #eee",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Warning
                style={{
                  height: "14px",
                  width: "14px",
                  marginRight: "6px",
                  color: "#ed6c02",
                }}
              />
              <span style={{ color: "#ed6c02", fontWeight: 500 }}>
                {data.significance}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  }
  return null;
};

export default function SelectedDistrictSalariesByPeriodChart({ data }) {
  if (!data || data.length === 0) {
    return (
      <div className="p-4 text-center">
        No data available for monthly salary distribution
      </div>
    );
  }

  const firstPeriodId = data.length > 0 ? data[0].periodId : "";
  const enhancedData = data.map((item, index) => {
    const formattedTotal = new Intl.NumberFormat("en-US").format(
      item.totalPaid
    );
    const totalInBillions = (item.totalPaid / 1000000000).toFixed(2);

    let changePercent = 0;
    let changeValue = 0;

    if (index > 0) {
      changeValue = item.totalPaid - data[index - 1].totalPaid;
      changePercent = (changeValue / data[index - 1].totalPaid) * 100;
    }

    let significance = null;
    if (item.totalPaid === Math.max(...data.map((d) => d.totalPaid))) {
      significance = "Highest";
    } else if (item.totalPaid === Math.min(...data.map((d) => d.totalPaid))) {
      significance = "Lowest";
    } else if (Math.abs(changePercent) > 5) {
      significance = "Significant Change";
    }

    return {
      ...item,
      formattedTotal,
      totalInBillions,
      changePercent: changePercent.toFixed(2),
      changeValue,
      significance,
      firstPeriodId,
    };
  });

  const formatToBillions = (value) => {
    return `RWF ${(value / 1000000000).toFixed(2)}B`;
  };

  const formatNumberWithCommas = (value) => {
    if (!value && value !== 0) return "0";
    const billion = 1000000000;

    if (value >= billion) {
      return `${(value / billion).toFixed(2)}B`;
    } else {
      return new Intl.NumberFormat("en-US").format(Math.round(value));
    }
  };

  const averageMonthly =
    data.reduce((acc, item) => acc + item.totalPaid, 0) / data.length;

  return (
    <div className="w-100">
      <div>
        <ResponsiveContainer width="100%" height={500}>
          <ComposedChart
            data={enhancedData}
            margin={{ top: 20, right: 5, left: 20, bottom: 60 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              horizontal={false}
              vertical={false}
            />
            <XAxis
              dataKey="periodName"
              interval={0}
              angle={-45}
              textAnchor="end"
              height={60}
              tick={{ fontSize: 15 }}
              padding={{ left: 10, right: 10 }}
            />
            <YAxis
              tickFormatter={formatToBillions}
              domain={[
                (dataMin) => Math.floor(dataMin * 0.95),
                (dataMax) => Math.ceil(dataMax * 1.05),
              ]}
            />
            <Tooltip content={<CustomTooltip />} />

            <ReferenceLine
              y={averageMonthly}
              stroke="#666"
              strokeDasharray="3 3"
              strokeWidth={2}
              opacity={0.7}
            >
              <Label
                value={`Avg: ${formatToBillions(averageMonthly)}`}
                position="insideBottomRight"
                fill="#666"
                fontSize={11}
              />
            </ReferenceLine>

            <Bar
              dataKey="totalPaid"
              name="Monthly Salary Expenditure"
              fill="hsl(var(--chart-1))"
              radius={[4, 4, 0, 0]}
              barSize={30}
            >
              {enhancedData.map((entry, index) => {
                let fillColor = "hsl(var(--chart-5))";

                if (entry.significance === "Highest") {
                  fillColor = "hsl(var(--chart-8))";
                } else if (entry.significance === "Lowest") {
                  fillColor = "hsl(var(--chart-6))";
                } else if (entry.significance === "Significant Change") {
                  fillColor = "hsl(var(--chart-2))";
                }

                return <Bar key={`bar-${index}`} fill={fillColor} />;
              })}

              <LabelList
                dataKey="totalInBillions"
                position="top"
                formatter={(value) => `RWF ${value}B`}
                style={{
                  fontSize: 10,
                  fill: "black",
                  fontWeight: "bold",
                }}
              />
            </Bar>
            <Line
              type="monotone"
              dataKey="totalPaid"
              stroke="hsl(var(--chart-2))"
              strokeWidth={2}
              dot={{ r: 4, strokeWidth: 2, fill: "white" }}
              activeDot={{ r: 6, strokeWidth: 2, stroke: "white" }}
              connectNulls
            />
          </ComposedChart>
        </ResponsiveContainer>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginBottom: "15px",
          }}
        >
          <div
            style={{
              fontSize: "13px",
              color: "#666",
              display: "flex",
              alignItems: "center",
            }}
          >
            <span
              style={{
                height: "10px",
                width: "10px",
                backgroundColor: "hsl(var(--chart-1))",
                display: "inline-block",
                marginRight: "5px",
                borderRadius: "2px",
              }}
            ></span>
            <span style={{ marginRight: "15px" }}>
              Monthly Expenditure (RWF, billions)
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
